'use client';

import React, { useMemo, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import useFetchProjectsProcurement from '../../hooks/useFetchProjectsProcurement';
import PoDataEntryModal from './Modals/PoDataEntryModal';
import PreflightPoModal from './Modals/PreflightPoModal';
import getPORequestColumns from '../../utils/getPORequestColumns';
import useUserMap from '../../hooks/useUserMap';
import ConfirmDialog from '../UserDialog/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { ProcurementAdminPermissions } from '../../utils/permissions';
import axios from 'axios';

import {
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  ModuleRegistry,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
]);

const calculateVendorColWidth = (data) => {
  if (!Array.isArray(data)) return 300;

  const maxLength = data.reduce((max, row) => {
    const vendors = row.appliedVendors || [];
    const longestVendor = vendors.reduce((vMax, v) => Math.max(vMax, v?.length || 0), 0);
    return Math.max(max, longestVendor);
  }, 0);

  // Estimate ~9px per character, clamp between 150 and 600
  return Math.min(600, Math.max(150, Math.ceil(maxLength * 7 + 100)));
};

const calculateProjectColWidth = (data) => {
  if (!Array.isArray(data)) return 300;

  const maxLength = data.reduce((max, row) => {
    const list = row.projectSubsidiaryNameList || [];
    const longest = list.reduce((vMax, v) => Math.max(vMax, v?.length || 0), 0);
    return Math.max(max, longest);
  }, 0);

  // Moderately conservative: ~10.5px per char + 100px buffer
  return Math.min(1000, Math.max(200, Math.ceil(maxLength * 7 + 100)));
};

const OpenRequests = ({ data, refetch }) => {
  const [gridApi, setGridApi] = useState(null);
  const { projects } = useFetchProjectsProcurement();
  const [editingRfq, setEditingRfq] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const userMap = useUserMap();

  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflightResult, setPreflightResult] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({ open: false });

  const { userPermissions, permsLoading } = useAuth();

  if (permsLoading) {
    return (
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
					<Typography variant="h6">Loading User Permissions</Typography>
      </Box>
    );
  }

  const isProcurementAdmin = useMemo(
    () =>
      userPermissions.some((perm) =>
        ProcurementAdminPermissions.includes(perm)
      ),
    [userPermissions]
  );

  const [newRequest, setNewRequest] = useState({
    name: '',
    description: '',
    vendorInstructions: '',
    deliveryLocation: '',
    workScope: '',
    formType: 'Equipment',
    proposalDueDate: new Date().toISOString(),
    desiredDeliveryDate: null,
    baseProjectId: '',
    baseSubsidiaryId: '',
    vendor: null,
  });

  useEffect(() => {
    setRowData(data || []);
  }, [data]);

  useEffect(() => {
    if (editingRfq) {
      setNewRequest({
        name: editingRfq.name || '',
        description: editingRfq.description || '',
        vendorInstructions: editingRfq.vendorInstructions || '',
        deliveryLocation: editingRfq.deliveryLocation || '',
        workScope: editingRfq.workScope || '',
        formType: editingRfq.formType || 'Equipment',
        proposalDueDate: editingRfq.proposalDueDate || new Date().toISOString(),
        desiredDeliveryDate: editingRfq.desiredDeliveryDate || null,
        baseProjectId: editingRfq.baseProjectId || '',
        baseSubsidiaryId: editingRfq.baseSubsidiaryId || '',
        vendor: null,
      });
    }
  }, [editingRfq]);

  const actionsRenderer = (params) => (
    <Box display="flex" alignItems="center" justifyContent="center" height="100%" gap={1}>
      <Tooltip title="Edit Request">
        <IconButton size="small" color="primary" onClick={() => handleEditLineItems(params.data)}>
          <SettingsIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Archive Request">
        <IconButton size="small" color="error" onClick={() => handleDeleteRfq(params.data)}>
          <DeleteIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const handleUpdateRfq = async () => {
    await refetch();
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false });
  };

  const handleDeleteRfq = (rfqRow) => {
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to archive "${rfqRow.name}"?`,
      onConfirm: async () => {
        try {
          await axios.put('/api/RfqHeader/rfq-isdelete', { id: rfqRow.id, isDeleted: true });
          // only runs if status is 2xx
          toast.success('Request archived successfully');
          await refetch();
        } catch (err) {
          const status = err.response?.status;
          if (status === 403) {
            toast.error('Elevated permissions required for this action.');
          } else {
            toast.error('Error archiving the request');
          }
        } finally {
          closeConfirmDialog();
        }
      },
      onCancel: closeConfirmDialog,
    });
  };

  const handleEditLineItems = async (poRequest) => {
    setEditingRfq(poRequest); // pass the entire request object
    setOpenDialog(true);
  };

  const vendorColWidth = useMemo(() => calculateVendorColWidth(rowData), [rowData]);
  const projectColWidth = useMemo(() => calculateProjectColWidth(rowData), [rowData]);

  const columnDefs = useMemo(
    () =>
      getPORequestColumns(
        userMap,
        actionsRenderer,
        vendorColWidth,
        projectColWidth,
        isProcurementAdmin,
        async (id, newNotes, oldNotes) => {
          try {
            await axios.put('/api/RfqHeader/update-approver-notes', { id, approverNotes: newNotes });
            toast.success('Approver notes saved');
            await refetch();
          } catch {
            toast.error('Approver notes save failed');
            gridApi.getRowNode(id).setDataValue('approverNotes', oldNotes);
          }
        }
      ),
    [userMap, actionsRenderer, vendorColWidth, projectColWidth, isProcurementAdmin]
  );

  const handleOpenDialog = () => setPreflightOpen(true);

  const handlePreflightComplete = (result) => {
    setPreflightResult(result);
    setNewRequest({
      name: result.rfqName,
      description: '',
      vendorInstructions: '',
      deliveryLocation: '',
      workScope: '',
      formType: 'Equipment',
      proposalDueDate: new Date().toISOString(),
      desiredDeliveryDate: null,
      baseProjectId: result.baseProjectId,
      baseSubsidiaryId: result.baseSubsidiaryId,
      vendor: null,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTimeout(() => {
      setEditingRfq(null);
      setNewRequest({
        name: '',
        description: '',
        vendorInstructions: '',
        deliveryLocation: '',
        workScope: '',
        formType: 'Equipment',
        proposalDueDate: '',
        desiredDeliveryDate: null,
        baseProjectId: '',
        baseSubsidiaryId: '',
        vendor: null,
      });
    }, 300);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRequest((prev) => ({ ...prev, [name]: value }));
  };

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    cellStyle: {
      textAlign: 'left',
    },
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '80vh',
        padding: 2,
      }}
    >
      <>
        <Box align="left" sx={{ mb: 1 }}>
          <Button variant="contained" color="primary" onClick={handleOpenDialog}>
            New Request
          </Button>
        </Box>

        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <div style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              columnDefs={columnDefs}
              rowGroupPanelShow={'always'}
              defaultColDef={defaultColDef}
              rowData={rowData}
              pagination={true}
              onGridReady={onGridReady}
            />
          </div>
        </Box>
      </>

      {/* Modals */}
      {openDialog && (
        <PoDataEntryModal
          key={editingRfq?.id ?? 'new'}
          open={true}
          onClose={handleCloseDialog}
          newRequest={editingRfq || newRequest}
          handleInputChange={handleInputChange}
          rfqId={editingRfq?.id}
          onUpdateRfq={refetch}
          preflight={editingRfq ? null : preflightResult}
        />
      )}
      {preflightOpen && (
        <PreflightPoModal
          open={preflightOpen}
          onClose={() => setPreflightOpen(false)}
          onConfirm={handlePreflightComplete}
          projects={projects}
        />
      )}
      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
        onClose={closeConfirmDialog}
      />
    </Box>
  );
};

export default OpenRequests;
